import React, { useEffect, useState } from "react";
import { Button } from "@heroui/react";
import { PageCard } from "../components/PageCard";
import { LoadingState } from "../components/LoadingState";
import { PortalSelect } from "../components/form/PortalSelect";
import { PortalTextArea } from "../components/form/PortalTextArea";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { navigatePortal } from "../lib/navigation";
import { showErrorToast, showSuccessToast } from "../lib/toast";
import { withdrawalStatusLabel } from "../lib/withdrawalStatus";

const decisionOptions = [
  { id: "approved", labelKey: "withdrawals.detail.decisionApproved" },
  { id: "rejected", labelKey: "withdrawals.detail.decisionRejected" },
  { id: "manual_review", labelKey: "withdrawals.detail.decisionManualReview" }
];

export function WithdrawalDetailPage({ path }) {
  const { t } = useLocale();
  const id = path.match(/\/portal\/withdrawals\/(\d+)/)?.[1];
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [rationale, setRationale] = useState("");
  const [ignoring, setIgnoring] = useState(false);

  const isFinal = detail && ["accepted", "rejected", "partially_accepted", "closed"].includes(detail.status);

  const load = () => {
    if (!id) return;
    setLoading(true);
    apiFetch(`/api/withdrawal_requests/${id}`)
      .then(setDetail)
      .catch(() => showErrorToast(t("withdrawals.table.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [id]);

  const submitIgnore = async () => {
    setIgnoring(true);
    try {
      await apiFetch(`/api/withdrawal_requests/${id}/ignore`, {
        method: "POST",
        body: JSON.stringify({ ignored_by: "portal_user", rationale })
      });
      showSuccessToast(t("withdrawals.detail.ignored"));
      navigatePortal("/portal/withdrawals");
    } catch {
      showErrorToast(t("withdrawals.detail.ignoreError"));
    } finally {
      setIgnoring(false);
    }
  };

  const submitDecision = async () => {
    try {
      await apiFetch(`/api/withdrawal_requests/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ decision, rationale, decided_by: "portal_user" })
      });
      showSuccessToast(t("withdrawals.detail.saved") || "Decision saved.");
      load();
    } catch {
      showErrorToast(t("withdrawals.detail.saveError") || "Could not save decision.");
    }
  };

  if (loading) return <LoadingState label={t("common.loading")} />;
  if (!detail) {
    return (
      <PageCard title={t("withdrawals.detail.notFound") || "Withdrawal not found"}>
        <Button variant="ghost" onPress={() => navigatePortal("/portal/withdrawals")}>
          {t("withdrawals.detail.back")}
        </Button>
      </PageCard>
    );
  }

  return (
    <div className="grid gap-4">
      <Button variant="ghost" className="w-fit" onPress={() => navigatePortal("/portal/withdrawals")}>
        {t("withdrawals.detail.back")}
      </Button>

      <PageCard title={t("withdrawals.detail.title", { caseId: detail.external_case_id })}>
        <p>
          <strong>{t("withdrawals.table.status")}:</strong> {withdrawalStatusLabel(t, detail.status)}
        </p>
        <p>
          <strong>{t("withdrawals.table.order")}:</strong> {detail.order_number || t("withdrawals.table.noOrder")}
        </p>
        <p>
          <strong>{t("withdrawals.table.customer")}:</strong> {detail.customer_email}
        </p>
        {detail.billing && (
          <p>
            <strong>{t("withdrawals.detail.billingStatusTitle")}:</strong>{" "}
            {detail.status === "closed" && !detail.billing?.usage_billed_at
              ? t("withdrawals.detail.billingStatus.notBilledIgnored")
              : detail.billing.usage_billed_at
              ? detail.billing.usage_billing_reason === "complimentary"
                ? t("withdrawals.detail.billingStatus.billedComplimentary")
                : detail.billing.usage_billing_reason === "merchant_decision"
                  ? t("withdrawals.detail.billingStatus.billedMerchantDecision")
                  : t("withdrawals.detail.billingStatus.billedOrderVerified")
              : detail.billing.order_verification_status === "pending" ||
                  detail.billing.order_verification_status === "lookup_failed"
                ? t("withdrawals.detail.billingStatus.notBilledPending")
                : t("withdrawals.detail.billingStatus.notBilledUnverified")}
          </p>
        )}

        {!isFinal && (
          <>
            <PortalSelect
              label={t("withdrawals.detail.decision")}
              value={decision}
              onChange={setDecision}
              options={decisionOptions.map((opt) => ({ id: opt.id, label: t(opt.labelKey) }))}
            />
            <PortalTextArea
              label={t("withdrawals.detail.rationale")}
              value={rationale}
              onChange={setRationale}
            />
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" className="portal-accent-button w-fit" onPress={submitDecision}>
                {t("withdrawals.detail.saveDecision")}
              </Button>
              <Button variant="ghost" className="w-fit" isDisabled={ignoring} onPress={submitIgnore}>
                {t("withdrawals.detail.ignore")}
              </Button>
            </div>
            <p className="text-sm text-default-500">{t("withdrawals.detail.ignoreHelp")}</p>
          </>
        )}
      </PageCard>
    </div>
  );
}
